/* eslint-disable max-lines */
import { Col, Table, Row, Button, FormGroup, Input, Label } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import FileDownload from 'js-file-download';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import TimeCardEditorModal from '../TimeCardEditorModal';
import AttendancesService from '../../../../services/Attendances';
import ReportService from '../../../../services/ClassReport';
import isAdmin from '../../../../helpers/isAdmin';
import isDirector from '../../../../helpers/isDirector';
import UserService from '../../../../services/User';
import getTimeCardInfo from '../../../../helpers/getTimeCardInfo';
import BlackoutDate from '../../../../services/BlackoutDate';
import Holidays from '../../../../services/Holidays';
import confirmAlertWithForm from '../../../../helpers/confirmAlertWithForm';
import OpacityLoaderSpinner from '../../../../components/Core/OpacityLoaderSpinner';
import WeekPicker from '../../../../components/Core/WeekPicker';

const TimeCardTab = () => {
  const { token, user, currentSchool } = useContext(AuthContext);
  const { id } = useParams<{ id: string }>();
  const [modal, setModal] = useState(false);
  const [dateSelected, setDateSelected] = useState('');
  const [data, setData] = useState([] as any[]);
  const [total, setTotal] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedWeekStart, setSelectedWeekStart] = useState<string>(moment().startOf('isoWeek').format('YYYY-MM-DD'));
  const isAdminUser = isAdmin(user);
  const isDisabledApprove = data
    .filter((d: any) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(d.date.substring(d.date.length - 3)))
    .some((d: any) => d.clockInIds.length === 0 || d.clockOutIds.length === 0);

  const [notWorkingDays, setNotWorkingDays] = useState(
    null as null | {
      blackouts: { reason: string; dates: string[] }[];
      hollidays: { reason: string; dates: string[] }[];
    },
  );
  const hollidayDates = notWorkingDays?.hollidays.map((h: any) => h.dates).flat();
  const blackoutDates = notWorkingDays?.blackouts.map((b: any) => b.dates).flat();

  useEffect(() => {
    loadList();
  }, [id, selectedWeekStart]);

  const calcDaysBetweenDates = (startDate: string, finalDate: string) => {
    const startDateMoment = moment(startDate);
    const finalDateMoment = moment(finalDate);
    const days = [] as string[];
    while (startDateMoment.isSameOrBefore(finalDateMoment)) {
      days.push(startDateMoment.format('YYYY-MM-DD'));
      startDateMoment.add(1, 'day');
    }
    return days;
  };

  const loadList = async () => {
    try {
      setLoading(true);
      const userResult = await UserService(token || '').show(id);
      const userDataParam = userResult?.data || {};

      const startDateParam = moment(selectedWeekStart).startOf('isoWeek').format('YYYY-MM-DD');
      const finalDateParam = moment(selectedWeekStart).endOf('isoWeek').format('YYYY-MM-DD');
      const startDateTime = `${startDateParam}T00:00:00.000Z`;
      const finalDateTime = `${finalDateParam}T23:59:59.000Z`;
      const result = await AttendancesService(token || '').listByUser(
        id,
        `startDateTime=${startDateTime}&finalDateTime=${finalDateTime}`,
      );

      const blackouts = await BlackoutDate(token || '').list(currentSchool.value);
      const hollidays = await Holidays(token || '').list(parseInt(moment().format('YYYY')), currentSchool.value);
      setNotWorkingDays({
        blackouts: blackouts.data.map((b: any) => ({
          reason: b.reason,
          dates: calcDaysBetweenDates(b.startDate, b.endDate).flat(),
        })),
        hollidays: hollidays.data.map((h: any) => ({
          reason: h.name,
          dates: calcDaysBetweenDates(h.startDate, h.endDate).flat(),
        })),
      });
      const data: any[] = result.data;
      setHeadersList(startDateParam, finalDateParam, userDataParam, data);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      return toast.error(message);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const setHeadersList = (startDate: string, finalDate: string, userDataParam: any, dataParam?: any[]) => {
    const { data, sumRealWorkTotal, sumStandardTime, sumOvertime, sumDailyOvertime } = getTimeCardInfo(
      startDate,
      finalDate,
      userDataParam,
      dataParam,
      currentSchool.timezone,
    );
    setTotal({
      title: 'TOTAL',
      standardTime: sumStandardTime ? sumStandardTime.toFixed(2) : 0,
      overtime: sumOvertime ? sumOvertime.toFixed(2) : 0,
      realWork: sumRealWorkTotal ? sumRealWorkTotal.toFixed(2) : 0,
      dailyOvertime: sumDailyOvertime.toFixed(2),
    });
    setData(data);
  };

  const toggleModal = (date?: string) => {
    if (!hasEditPermission()) {
      return;
    }
    const newValue = !modal;
    setModal(newValue);
    if (newValue) {
      setDateSelected(date || '');
      return;
    }
    setDateSelected('');
  };

  const hasEditPermission = () => {
    if (isAdmin(user)) {
      return true;
    }
    if (isDirector(user) && id !== user?.id) {
      return true;
    }
    return false;
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      const startDateParam = moment(selectedWeekStart).startOf('isoWeek').format('YYYY-MM-DD');
      const finalDateParam = moment(selectedWeekStart).endOf('isoWeek').format('YYYY-MM-DD');
      const startDateTime = `${startDateParam}T00:00:00.000Z`;
      const finalDateTime = `${finalDateParam}T23:59:59.000Z`;
      const response = await ReportService(token || '').timecard(
        id,
        `startDateTime=${startDateTime}&finalDateTime=${finalDateTime}`,
      );
      FileDownload(response, 'time-card.pdf');
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Report error!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTimeCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      await AttendancesService(token || '').approveTimeCard(
        data.map((d: any) => [...d.clockInIds, ...d.clockOutIds]).flat(),
      );
      toast.success('Timecard approved successfully');
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error approving time card!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitError = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const reason = (form.querySelector('[name="reason"]') as HTMLInputElement)?.value?.trim();
    if (!reason) {
      toast.error('Reason is required');
      return;
    }
    const ids = data.map((d: any) => [...d.clockInIds, ...d.clockOutIds]).flat();
    try {
      await AttendancesService(token || '').denyTimeCard(ids, reason);
      toast.success('Timecard submitted as error successfully');
      loadList();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error submitting timecard as error');
    }
  };

  const openSubmitErrorModal = () => {
    if (!hasEditPermission()) return;
    confirmAlertWithForm({
      title: 'Submit an Error',
      messageType: 'DELETE',
      message: 'Enter the reason for reporting an error on this timecard.',
      children: (
        <Row>
          <Col>
            <FormGroup className="form-control-label">
              <Label>Reason</Label>
              <Input type="textarea" name="reason" required />
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: handleSubmitError,
    });
  };

  if (initialLoading) {
    return <OpacityLoaderSpinner />;
  }

  return (
    <>
      <Row className="mb-3 align-items-start">
        <Col lg="4">
          <WeekPicker value={selectedWeekStart} onChange={(weekStart) => setSelectedWeekStart(weekStart)} />
        </Col>
        {isAdminUser && (
          <Col lg="2" className="mt-2 mt-lg-0">
            <Button color="primary" onClick={(e) => handleReportSubmit(e)}>
              <i className="fas fa-print"></i>
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Table className="align-items-center table-flush mb-3" responsive>
          <tbody>
            <tr>
              <th>Date</th>
              <th>In</th>
              <th>Out</th>
              <th>Pay Code</th>
              {isAdminUser && (
                <>
                  <th>Standard Time</th>
                  <th>Overtime</th>
                  <th>Daily Overtime</th>
                </>
              )}
              <th>Real Work</th>
            </tr>
            {data.map((d, index) =>
              blackoutDates?.includes(d.dateSelected) ? (
                <tr key={`data-key-${index}`} style={{ backgroundColor: '#172b4d', color: 'white' }}>
                  <td>{d.date}</td>
                  <td colSpan={7}>
                    {notWorkingDays?.blackouts.find((b: any) => b.dates.includes(d.dateSelected))?.reason}
                  </td>
                </tr>
              ) : hollidayDates?.includes(d.dateSelected) ? (
                <tr
                  key={`data-key-${index}`}
                  style={{ backgroundColor: '#e4605e', color: 'white', borderColor: '#e4605e' }}
                >
                  <td>{d.date}</td>
                  <td colSpan={7}>
                    {notWorkingDays?.hollidays.find((h: any) => h.dates.includes(d.dateSelected))?.reason}
                  </td>
                </tr>
              ) : (
                <tr onClick={() => toggleModal(d.dateSelected)} style={{ cursor: 'pointer' }} key={`data-key-${index}`}>
                  <td>{d.date}</td>
                  <td>
                    {d.clockIn.map((ci: string, index: number) =>
                      index === 0 ? (
                        <span key={`data-ci-key-${index}`}>{ci}</span>
                      ) : (
                        <span key={`data-ci-key-${index}`}>
                          <br />
                          {ci}
                        </span>
                      ),
                    )}
                  </td>
                  <td>
                    {d.clockOut.map((co: string, index: number) =>
                      index === 0 ? (
                        <span key={`data-co-key-${index}`}>{co}</span>
                      ) : (
                        <span key={`data-co-key-${index}`}>
                          <br />
                          {co}
                        </span>
                      ),
                    )}
                  </td>
                  <td>
                    {d.payCode.map((value: string, index: number) =>
                      index === 0 ? (
                        <span key={`data-co-key-${index}`}>{value ? value : 'Regular Hours'}</span>
                      ) : (
                        <span key={`data-co-key-${index}`}>
                          <br />
                          {value ? value : 'Regular Hours'}
                        </span>
                      ),
                    )}
                  </td>
                  {isAdminUser && (
                    <>
                      <td>{d.standardTime}</td>
                      <td>{d.overtime}</td>
                      <td>{d.dailyOvertime}</td>
                    </>
                  )}
                  <td>{d.realWork}</td>
                </tr>
              ),
            )}
            {total && (
              <tr>
                <td>
                  <b>{total.title}</b>
                </td>
                <td></td>
                <td></td>
                {isAdminUser && (
                  <>
                    <td>
                      <b>{total.standardTime}</b>
                    </td>
                    <td>
                      <b>{total.overtime}</b>
                    </td>
                    <td>
                      <b>{total.dailyOvertime}</b>
                    </td>
                  </>
                )}
                <td>
                  <b>{total.realWork}</b>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Row>
      <Row>
        <Col xs="6">
          <Button color="danger" onClick={openSubmitErrorModal} disabled={!hasEditPermission()}>
            <i className="fas fa-exclamation-triangle" />
            &nbsp; Submit an Error
          </Button>
        </Col>
        <Col xs="6 text-right">
          <Button color="primary" onClick={(e) => handleApproveTimeCard(e)} disabled={isDisabledApprove}>
            <i className="fas fa-check" />
            &nbsp; Approve
          </Button>
        </Col>
      </Row>
      <TimeCardEditorModal
        userId={id}
        isOpen={modal}
        dateSelected={dateSelected}
        toggleModal={() => {
          setModal(!modal);
          loadList();
        }}
      />
    </>
  );
};

export default TimeCardTab;
